import { redirect } from "@tanstack/react-router";
import { useFeatureStore } from "@/stores/useFeatureStore";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { ensureMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import type { PermissionCode } from "@/types/authorization";
import type { OrgSettings } from "@/types/organization/settings";

export function requireFeature(key: string) {
  if (!useFeatureStore.getState().isEnabled(key)) {
    throw redirect({ to: "/unauthorized" });
  }
}

export function requireSetting(key: keyof OrgSettings) {
  if (!useOrgSettingsStore.getState().settings?.[key]) {
    throw redirect({ to: "/unauthorized" });
  }
}

/** Allow access when at least one of the given org settings is enabled. */
export function requireAnySetting(keys: (keyof OrgSettings)[]) {
  const settings = useOrgSettingsStore.getState().settings;
  if (!keys.some((key) => settings?.[key])) {
    throw redirect({ to: "/unauthorized" });
  }
}

/**
 * Restrict a route to holders of a capability. **Presentation only** — the
 * server re-checks on every request; this just avoids showing a door that will
 * not open.
 *
 * Async because the capability set is not cached yet on a hard refresh, and
 * bouncing a permitted user to /unauthorized on reload is worse than waiting.
 * Callers must `await` it.
 */
export async function requirePermission(code: PermissionCode) {
  const capabilities = await ensureMyPermissions();
  if (!can(capabilities, code)) {
    throw redirect({ to: "/unauthorized" });
  }
}
